import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import { API_ENDPOINTS } from '../services/api/apiConfig';

// Hook simple pour le cache des patients
export function useCachedPatientSimple(id: string | null) {
  const [patient, setPatient] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache simple avec localStorage
  const getCacheKey = useCallback((id: string | null) => id ? `patient_${id}` : '', []);

  const getCachedPatient = useCallback(() => {
    try {
      if (!id) return null;
      const cached = localStorage.getItem(getCacheKey(id));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [id, getCacheKey]);

  const setCachedPatient = useCallback((data: any) => {
    try {
      if (!data || !data.id) return;
      localStorage.setItem(getCacheKey(data.id), JSON.stringify(data));
      // Expirer après 10 minutes
      setTimeout(() => {
        localStorage.removeItem(getCacheKey(data.id));
      }, 10 * 60 * 1000);
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [getCacheKey]);

  const fetchPatient = useCallback(async () => {
    if (!id) return;

    // Vérifier le cache d'abord
    const cached = getCachedPatient();
    if (cached) {
      setPatient(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(
        API_ENDPOINTS.PATIENTS.GET_BY_ID(Number(id))
      );

      const patientData = response.data;
      setPatient(patientData);
      setCachedPatient(patientData); // Mettre en cache

    } catch (err) {
      setError("Erreur lors de la récupération du patient");
      console.error("Error fetching patient:", err);
    } finally {
      setLoading(false);
    }
  }, [id, getCachedPatient, setCachedPatient]);

  const refetch = useCallback(() => {
    // Invalider le cache et recharger
    const cacheKey = id ? getCacheKey(id) : null;
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
    }
    fetchPatient();
  }, [id, fetchPatient, getCacheKey]);

  return { patient, loading, error, refetch };
}

// Hook simple pour le cache des médecins
export function useCachedDoctorSimple() {
  const [doctor, setDoctor] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache simple avec localStorage
  const getCacheKey = useCallback(() => 'doctor_all', []);

  const getCachedDoctor = useCallback(() => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [getCacheKey]);

  const setCachedDoctor = useCallback((data: any) => {
    try {
      localStorage.setItem(getCacheKey(), JSON.stringify(data));
      // Expirer après 15 minutes
      setTimeout(() => {
        localStorage.removeItem(getCacheKey());
      }, 15 * 60 * 1000);
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [getCacheKey]);

  const fetchDoctor = useCallback(async () => {
    // Vérifier le cache d'abord
    const cached = getCachedDoctor();
    if (cached) {
      setDoctor(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(
        API_ENDPOINTS.USERS.DOCTORS.SORTED
      );

      const doctorData = response.data || [];
      setDoctor(doctorData);
      setCachedDoctor(doctorData); // Mettre en cache

    } catch (err) {
      setError("Erreur lors de la récupération du médecin");
      console.error("Error fetching doctor:", err);
    } finally {
      setLoading(false);
    }
  }, [getCachedDoctor, setCachedDoctor]);

  const refetch = useCallback(() => {
    // Invalider le cache et recharger
    localStorage.removeItem(getCacheKey());
    fetchDoctor();
  }, [fetchDoctor, getCacheKey]);

  return { doctor, loading, error, refetch };
}

// Hook simple pour le cache des dossiers médicaux
export function useCachedMedicalRecordSimple(id: string | null | undefined) {
  const [record, setRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCacheKey = useCallback((id: string | null | undefined) => id ? `medical_record_${id}` : '', []);

  const getCachedRecord = useCallback(() => {
    try {
      if (!id) return null;
      const cached = localStorage.getItem(getCacheKey(id));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [id, getCacheKey]);

  const setCachedRecord = useCallback((data: any) => {
    try {
      if (!data || !data.id) return;
      localStorage.setItem(getCacheKey(data.id), JSON.stringify(data));
      // Expirer après 5 minutes
      setTimeout(() => {
        localStorage.removeItem(getCacheKey(data.id));
      }, 5 * 60 * 1000);
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [getCacheKey]);

  const fetchRecord = useCallback(async () => {
    if (!id) return;

    const cached = getCachedRecord();
    if (cached) {
      setRecord(cached);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(API_ENDPOINTS.MEDICAL_RECORDS.GET_BY_ID(Number(id)));
      const recordData = response.data;
      setRecord(recordData);
      setCachedRecord(recordData);
    } catch (err) {
      setError("Erreur lors de la récupération du dossier médical");
      console.error("Error fetching medical record:", err);
    } finally {
      setLoading(false);
    }
  }, [id, getCachedRecord, setCachedRecord]);

  const refetch = useCallback(() => {
    const cacheKey = id ? getCacheKey(id) : null;
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
    }
    fetchRecord();
  }, [id, fetchRecord, getCacheKey]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  return { record, loading, error, refetch };
}
